import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { computeDeltaDisplay } from '../../utils/reportDelta';

/**
 * Story 21-A — one report summary tile: value + a "vs last month" delta chip.
 * `metric` is a MetricComparison ({ thisMonth, lastMonth, deltaAbsolute, deltaPercent }); a null
 * deltaPercent (no prior-month data) renders "—" rather than a broken/infinite change.
 * Delta coloring/direction logic lives in utils/reportDelta so the staff scorecard table's
 * inline deltas (Story 21-B) can't drift from this tile's rules.
 */
function ReportMetricTile({ icon, label, valueDisplay, metric, coloring = 'neutral' }) {
  const { t } = useTranslation('reports');
  const { hasDelta, deltaClass, direction, absPercent } = computeDeltaDisplay(metric, coloring);
  const DeltaIcon = direction === 'up' ? TrendingUp : direction === 'down' ? TrendingDown : null;

  return (
    <div className="rounded-xl bg-white border border-gray-200 shadow-sm p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2 text-gray-500">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <span className="text-2xl font-semibold text-gray-900">{valueDisplay}</span>
      <div className="flex items-center gap-1 text-xs">
        <span className={`inline-flex items-center gap-0.5 font-medium ${deltaClass}`}>
          {DeltaIcon && <DeltaIcon size={12} />}
          {hasDelta ? `${absPercent.toFixed(1)}%` : t('summary.noDelta')}
        </span>
        <span className="text-gray-400">{t('summary.vsLastMonth')}</span>
      </div>
    </div>
  );
}

export default ReportMetricTile;
